import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, Ban, Trash2, Phone, MapPin, Globe, Mail, Calendar, Eye, KeyRound, CreditCard, Plus, Copy, HardDrive, Users as UsersIcon, ShieldCheck, MailWarning } from 'lucide-react';
import {
  getFirmDetailStats, getFirmMatters, getFirmContacts, getFirmInvoices,
  suspendFirm, unsuspendFirm, softDeleteFirm, restoreFirm, purgeFirm,
  saExtendFirmTrial, saSetFirmPlan, saResetFirmAdminPassword,
  saSetFirmSeatLimit, saSetFirmStorageLimit, saAddFirmStorageAddon,
} from '../../lib/db';
import { formatDate, formatMoney, formatDuration } from '../../lib/formatters';
import { PLANS } from '../../lib/plans';
import { useRoute } from '../../lib/router';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import toast from '../../lib/toast';
import CapacityPickerModal from '../../components/superadmin/CapacityPickerModal';

const Stat = ({ label, value }) => (
  <div className="bg-slate-800 rounded-xl p-4">
    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-xl font-bold text-white">{value}</p>
  </div>
);

const TABS = ['Overview', 'Users', 'Case Files', 'Contacts', 'Invoices'];

// Role pill colors — mirror what Settings → Team uses so the same role
// reads the same everywhere in the app.
const ROLE_COLOR = {
  'Managing Partner': 'bg-purple-900/40 text-purple-300',
  'Partner':          'bg-indigo-900/40 text-indigo-300',
  'Senior Associate': 'bg-blue-900/40 text-blue-300',
  'Associate':        'bg-sky-900/40 text-sky-300',
  'Paralegal':        'bg-emerald-900/40 text-emerald-300',
  'Legal Secretary':  'bg-teal-900/40 text-teal-300',
  'Admin':            'bg-amber-900/40 text-amber-300',
};

// A user counts as "active" if email_confirmed=true OR they have a
// last_login timestamp. Matches the bucket logic SettingsPage uses
// at line ~969 so the two pages never disagree about who counts.
const userIsActive = (u) =>
  u.email_confirmed === true || !!u.last_login;

const MATTER_STATUS_COLOR = {
  open:        'bg-blue-900/40 text-blue-300',
  in_progress: 'bg-amber-900/40 text-amber-300',
  closed:      'bg-slate-700 text-slate-400',
  archived:    'bg-slate-700 text-slate-500',
};

const INVOICE_STATUS_COLOR = {
  draft:   'bg-slate-700 text-slate-400',
  sent:    'bg-blue-900/40 text-blue-300',
  paid:    'bg-emerald-900/40 text-emerald-300',
  overdue: 'bg-red-900/40 text-red-300',
  void:    'bg-slate-700 text-slate-500',
};

export default function SuperAdminFirmPage({ firmId }) {
  const [stats,    setStats]    = useState(null);
  const [firmRow,  setFirmRow]  = useState(null);
  const [matters,  setMatters]  = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('Overview');
  const [working,  setWorking]  = useState(false);
  const [, navigate] = useRoute();
  const { start: startImpersonate } = useImpersonation();

  useEffect(() => {
    if (!firmId) return;
    Promise.all([
      getFirmDetailStats(firmId),
      getFirmMatters(firmId),
      getFirmContacts(firmId),
      getFirmInvoices(firmId),
      import('../../services/supabaseClient').then(({ supabase }) =>
        supabase.from('firms').select('*').eq('id', firmId).maybeSingle()
      ),
    ]).then(([statsRes, mattersRes, contactsRes, invoicesRes, firmRes]) => {
      setStats(statsRes.data || {});
      setMatters(mattersRes.data || []);
      setContacts(contactsRes.data || []);
      setInvoices(invoicesRes.data || []);
      setFirmRow(firmRes.data || null);
      setLoading(false);
    });
  }, [firmId]);

  const handleSuspend = async () => {
    const reason = window.prompt('Reason for suspension (optional):');
    if (reason === null) return;
    setWorking(true);
    // sa_suspend_firm RPC enforces super-admin AND server-side blocks
    // ALL data access for users of the suspended firm — `current_firm_id()`
    // now returns NULL for them, fail-closing every firm-scoped RLS
    // policy in the database. The user's auth session is left intact
    // (so unsuspending immediately restores access without forcing a
    // re-login), but every read/write fails until restored.
    const { data, error } = await suspendFirm(firmId, reason);
    setWorking(false);
    if (error) { toast.error('Suspend failed: ' + error.message); return; }
    setFirmRow(data || ((f) => ({
      ...f,
      suspended_at:     new Date().toISOString(),
      suspended_reason: reason,
      subscription_status: 'suspended',
    })));
    toast.success('Firm suspended. All access blocked server-side.');
  };

  const handleUnsuspend = async () => {
    if (!window.confirm('Restore access for this firm?')) return;
    setWorking(true);
    const { data, error } = await unsuspendFirm(firmId);
    setWorking(false);
    if (error) { toast.error('Unsuspend failed: ' + error.message); return; }
    // RPC restores `trial` or `active` based on trial_ends_at — not
    // hardcoded to 'active' like the old flow which silently extended
    // trials.
    setFirmRow(data || null);
    toast.success('Access restored.');
  };

  const handleViewAs = async () => {
    if (!firmRow?.name) return;
    if (!window.confirm(`View the app as "${firmRow.name}"? Your session will switch to that firm for up to 4 hours.`)) return;
    setWorking(true);
    try {
      await startImpersonate(firmId);
      window.location.hash = '#/';
      window.location.reload();
    } catch (err) {
      setWorking(false);
      toast.error(err?.message || 'Could not start impersonation.');
    }
  };

  // Two-tier deletion (migration 132):
  //   * Soft delete — marks the firm, blocks all access, preserves
  //     data for 30 days. Reversible via handleRestore.
  //   * Purge      — irreversible hard delete. Only allowed AFTER a
  //     soft delete, and requires the firm name as confirmation.
  // The old single-click "Delete" path was a foot-cannon — one
  // mistyped click and a paying customer's data was gone with no
  // recovery and no auth.users cleanup.
  const handleSoftDelete = async () => {
    const name = firmRow?.name || 'this firm';
    const reason = window.prompt(
      `Soft-delete "${name}"?\n\n`
      + 'This blocks all access immediately and preserves the data for 30 days. '
      + 'You can restore the firm during that window. After 30 days you (or another '
      + 'super-admin) must explicitly Purge to actually wipe the data.\n\n'
      + 'Reason (logged to the audit trail):'
    );
    if (reason === null) return; // cancelled
    setWorking(true);
    const { data, error } = await softDeleteFirm(firmId, reason);
    setWorking(false);
    if (error) { toast.error('Soft delete failed: ' + error.message); return; }
    setFirmRow(data || null);
    toast.success(`${name} soft-deleted. Restore within 30 days from the firm page.`);
  };

  const handleRestore = async () => {
    if (!window.confirm('Restore this firm? Users will regain access immediately.')) return;
    setWorking(true);
    const { data, error } = await restoreFirm(firmId);
    setWorking(false);
    if (error) { toast.error('Restore failed: ' + error.message); return; }
    setFirmRow(data || null);
    toast.success('Firm restored.');
  };

  const handlePurge = async () => {
    const name = firmRow?.name || '';
    if (!firmRow?.deleted_at) {
      toast.warning('Soft-delete the firm first. Purge is irreversible and requires the two-step workflow.');
      return;
    }
    const typed = window.prompt(
      `PERMANENT DELETION.\n\n`
      + `This will hard-delete every row belonging to "${name}" — matters, `
      + `contacts, invoices, time entries, documents, messages, employees — `
      + `with no recovery path. Auth accounts for every firm user will be `
      + `queued for deletion.\n\n`
      + `Type the firm name exactly to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== name.trim()) {
      toast.warning('Name did not match. Cancelled.');
      return;
    }
    setWorking(true);
    const { data, error } = await purgeFirm(firmId, typed);
    setWorking(false);
    if (error) {
      toast.error('Purge failed: ' + error.message);
      return;
    }
    const userCount = data?.user_ids_to_clean?.length || 0;
    toast.success(
      `Firm purged.${userCount ? ` ${userCount} auth account${userCount === 1 ? '' : 's'} queued for cleanup.` : ''}`
    );
    navigate('/superadmin');
  };

  const handleExtendTrial = async () => {
    const raw = window.prompt('Extend trial by how many days? (1–365)', '14');
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.warning('Enter a whole number between 1 and 365.');
      return;
    }
    setWorking(true);
    const { data, error: err } = await saExtendFirmTrial(firmId, days);
    setWorking(false);
    if (err) { toast.error(err.message || 'Could not extend trial'); return; }
    const newEnd = data?.trial_ends_at;
    setFirmRow((f) => ({
      ...f,
      trial_ends_at:        newEnd || f?.trial_ends_at,
      subscription_status:  f?.subscription_status === 'suspended' ? 'trial' : f?.subscription_status,
      suspended_at:         null,
      suspended_reason:     null,
    }));
    toast.success(`Trial extended by ${days} day${days === 1 ? '' : 's'}.`);
  };

  const handleChangePlan = async (newPlan) => {
    if (!newPlan || newPlan === firmRow?.subscription_plan) return;
    if (!window.confirm(`Change subscription plan to "${newPlan}"?`)) return;
    setWorking(true);
    const { error: err } = await saSetFirmPlan(firmId, newPlan);
    setWorking(false);
    if (err) { toast.error(err.message || 'Could not change plan'); return; }
    setFirmRow((f) => ({ ...f, subscription_plan: newPlan }));
    toast.success(`Plan changed to ${newPlan}.`);
  };

  // ── Capacity controls (migrations 141 + 143) ─────────────────────
  // Modal-based pickers replaced the window.prompt() approach so
  // super-admins choose from preset tiers (1 / 5 / 10 / 25 / 50 /
  // 100 / 200 / 500 GB / 1 TB / Unlimited) rather than typing free
  // text. "Custom" preset is still available for one-off deals.
  // capacityModal === null means closed; otherwise it's one of:
  //   'seat' | 'storage-base' | 'storage-addon'
  const [capacityModal, setCapacityModal] = useState(null);

  const handleSubmitSeatLimit = async (newLimit) => {
    setWorking(true);
    const { error: err } = await saSetFirmSeatLimit(firmId, newLimit);
    setWorking(false);
    if (err) { toast.error(err.message || 'Could not update seat limit'); return; }
    setFirmRow((f) => ({ ...f, seat_limit: newLimit }));
    setStats((s) => ({ ...s, seat_limit: newLimit }));
    toast.success(newLimit === null ? 'Seat cap removed (unlimited).' : `Seat limit set to ${newLimit}.`);
    setCapacityModal(null);
  };

  // GB → MB conversion happens here; the modal returns GB (or null).
  const handleSubmitStorageLimit = async (newGb) => {
    const limitMb = newGb == null ? null : Math.round(newGb * 1024);
    setWorking(true);
    const { error: err } = await saSetFirmStorageLimit(firmId, limitMb);
    setWorking(false);
    if (err) { toast.error(err.message || 'Could not update storage limit'); return; }
    setStats((s) => ({ ...s, storage_limit_mb: limitMb }));
    toast.success(
      limitMb === null
        ? 'Storage cap removed (unlimited).'
        : `Storage limit set to ${(limitMb / 1024).toFixed(limitMb >= 10240 ? 0 : 1)} GB.`,
    );
    setCapacityModal(null);
  };

  // Add-ons stack (migration 143). RPC returns the NEW total addon
  // in MB so we can update local state without refetching.
  const handleSubmitStorageAddon = async (addedGb) => {
    const extraMb = Math.round(addedGb * 1024);
    setWorking(true);
    const { data, error: err } = await saAddFirmStorageAddon(firmId, extraMb);
    setWorking(false);
    if (err) { toast.error(err.message || 'Could not add storage'); return; }
    setStats((s) => ({ ...s, storage_addon_mb: data ?? (s?.storage_addon_mb || 0) + extraMb }));
    toast.success(`Added ${addedGb} GB. Firm's add-on is now ${((data ?? extraMb) / 1024).toFixed(1)} GB.`);
    setCapacityModal(null);
  };

  // Open-modal shortcuts referenced from the Plan capacity card buttons.
  const handleSetSeatLimit     = () => setCapacityModal('seat');
  const handleSetStorageLimit  = () => setCapacityModal('storage-base');
  const handleAddStorageAddon  = () => setCapacityModal('storage-addon');

  const handleResetPassword = async (user) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
    if (!window.confirm(`Send a password-reset email to ${name} (${user.email})?`)) return;
    setWorking(true);
    const { data, error: err } = await saResetFirmAdminPassword(user.id);
    setWorking(false);
    const errMsg = err?.message || data?.error;
    if (errMsg) { toast.error(errMsg); return; }
    toast.success(`Password-reset email sent to ${user.email}.`);
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed'),
    );
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;

  const s = stats || {};
  // `suspended_at` is set both for a plain suspension AND for a
  // soft-delete (which is suspension + a deletion marker). Distinguish
  // the two so the action bar shows the right buttons.
  const isSuspended    = !!firmRow?.suspended_at;
  const isSoftDeleted  = !!firmRow?.deleted_at;
  // Days remaining in the soft-delete grace window. After this expires
  // the firm appears in sa_list_firms_due_for_purge().
  const daysSinceDelete = isSoftDeleted
    ? Math.floor((Date.now() - new Date(firmRow.deleted_at).getTime()) / 86_400_000)
    : 0;
  const daysUntilPurge = Math.max(30 - daysSinceDelete, 0);

  return (
    <div className="space-y-6">
      {/* Back */}
      <button onClick={() => navigate('/superadmin')}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition">
        <ArrowLeft size={14} /> Back to dashboard
      </button>

      {/* Header card */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isSoftDeleted
                ? <span className="text-xs px-2.5 py-1 rounded-full bg-red-900/60 text-red-300 font-medium">Soft-deleted</span>
                : isSuspended
                ? <span className="text-xs px-2.5 py-1 rounded-full bg-red-900/40 text-red-400 font-medium">Suspended</span>
                : <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 text-emerald-400 font-medium">Active</span>
              }
            </div>
            <h1 className="text-xl font-semibold text-white">{firmRow?.name || firmId}</h1>
            <p className="text-slate-400 text-sm mt-0.5">
              {firmRow?.email || '—'} · Created {formatDate(firmRow?.created_at)}
            </p>
            {isSoftDeleted && (
              <p className="text-red-300 text-xs mt-1">
                Soft-deleted {formatDate(firmRow.deleted_at)} ·
                {daysUntilPurge > 0
                  ? <> data preserved <strong>{daysUntilPurge}</strong> more day{daysUntilPurge === 1 ? '' : 's'}, then eligible for purge</>
                  : <> retention window expired — eligible for purge now</>
                }
                {firmRow.suspended_reason ? ` · ${firmRow.suspended_reason}` : ''}
              </p>
            )}
            {!isSoftDeleted && isSuspended && (
              <p className="text-red-400 text-xs mt-1">
                Suspended {formatDate(firmRow.suspended_at)}
                {firmRow.suspended_reason ? ` · ${firmRow.suspended_reason}` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* "View as" stays available even on suspended/soft-deleted
                firms — super-admin impersonation bypasses the
                current_firm_id() mask in migration 132 so we can
                still investigate. */}
            <button onClick={handleViewAs} disabled={working}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-600 text-white text-sm font-medium transition disabled:opacity-50">
              <Eye size={14} /> View as firm
            </button>
            {isSoftDeleted ? (
              <>
                {/* Soft-deleted: offer restore (reverses everything) or
                    purge (only after retention window expires). The
                    purge button is destructive-styled and gated by
                    daysUntilPurge so accidental clicks can't escalate. */}
                <button onClick={handleRestore} disabled={working}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition disabled:opacity-50">
                  <CheckCircle size={14} /> Restore firm
                </button>
                <button onClick={handlePurge} disabled={working || daysUntilPurge > 0}
                  title={daysUntilPurge > 0
                    ? `Purge unlocks in ${daysUntilPurge} day${daysUntilPurge === 1 ? '' : 's'}`
                    : 'Permanently delete this firm and all data'}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-800 hover:bg-red-700 text-white text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash2 size={14} /> Purge permanently
                </button>
              </>
            ) : isSuspended ? (
              <>
                <button onClick={handleUnsuspend} disabled={working}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition disabled:opacity-50">
                  <CheckCircle size={14} /> Restore access
                </button>
                <button onClick={handleSoftDelete} disabled={working}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-700 hover:bg-red-600 text-white text-sm font-medium transition disabled:opacity-50">
                  <Trash2 size={14} /> Soft-delete
                </button>
              </>
            ) : (
              <>
                <button onClick={handleSuspend} disabled={working}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-700 hover:bg-amber-600 text-white text-sm font-medium transition disabled:opacity-50">
                  <Ban size={14} /> Suspend
                </button>
                <button onClick={handleSoftDelete} disabled={working}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-700 hover:bg-red-600 text-white text-sm font-medium transition disabled:opacity-50">
                  <Trash2 size={14} /> Soft-delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
            {t}
            {t === 'Users' && <span className="ml-1.5 text-xs text-slate-500">{(s.users || []).length}</span>}
            {t === 'Case Files' && <span className="ml-1.5 text-xs text-slate-500">{matters.length}</span>}
            {t === 'Contacts' && <span className="ml-1.5 text-xs text-slate-500">{contacts.length}</span>}
            {t === 'Invoices' && <span className="ml-1.5 text-xs text-slate-500">{invoices.length}</span>}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'Overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Users"        value={s.user_count     || 0} />
            {/* Seats: only render when the firm has a seat cap
                configured (NULL = unlimited / enterprise). Shown as
                "in_use / limit" to mirror the dashboard table. */}
            {s.seat_limit != null && (
              <Stat
                label="Seats"
                value={`${s.seats_in_use ?? 0} / ${s.seat_limit}`}
              />
            )}
            {/* Storage: same conditional rendering. Display in GB if
                ≥ 1 GB on either side, else MB. addon_mb (if any) gets
                folded into the limit so the displayed cap matches
                what the trigger actually enforces. */}
            {s.storage_limit_mb != null && (() => {
              const usedMb     = Number(s.storage_used_mb  || 0);
              const effectMb   = Number(s.storage_limit_mb || 0)
                              + Number(s.storage_addon_mb || 0);
              const fmt = (mb) =>
                mb >= 1024
                  ? `${(mb / 1024).toFixed(mb >= 10240 ? 0 : 1)} GB`
                  : `${mb} MB`;
              return (
                <Stat
                  label="Storage"
                  value={`${fmt(usedMb)} / ${fmt(effectMb)}`}
                />
              );
            })()}
            <Stat label="Case Files"      value={s.matter_count   || 0} />
            <Stat label="Open case files" value={s.open_matters   || 0} />
            <Stat label="Contacts"     value={s.contact_count  || 0} />
            <Stat label="Invoices"     value={s.invoice_count  || 0} />
            <Stat label="Total billed" value={formatMoney(s.invoice_total || 0)} />
            <Stat label="Total paid"   value={formatMoney(s.paid_total    || 0)} />
            <Stat label="Time logged"  value={formatDuration(s.time_minutes || 0)} />
          </div>

          {/* Subscription & billing card */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <CreditCard size={14} className="text-indigo-400" />
                Subscription
              </h2>
              <button
                onClick={handleExtendTrial}
                disabled={working}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium transition disabled:opacity-50"
              >
                <Plus size={12} /> Extend trial
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Status</p>
                <p className="text-slate-200 capitalize">{firmRow?.subscription_status || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Plan</p>
                <select
                  value={firmRow?.subscription_plan || 'trial'}
                  onChange={(e) => handleChangePlan(e.target.value)}
                  disabled={working}
                  className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="trial">Trial</option>
                  <option value="basic">Basic ({PLANS.basic.priceGhs ? `GHS ${PLANS.basic.priceGhs}/mo` : '—'})</option>
                  <option value="pro">Pro ({PLANS.pro.priceGhs ? `GHS ${PLANS.pro.priceGhs}/mo` : '—'})</option>
                  <option value="ai">Enterprise</option>
                </select>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Trial ends</p>
                <p className="text-slate-200">
                  {firmRow?.trial_ends_at
                    ? new Date(firmRow.trial_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : <span className="text-slate-600">—</span>}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Next renewal</p>
                <p className="text-slate-200">
                  {firmRow?.subscription_renews_at
                    ? new Date(firmRow.subscription_renews_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : <span className="text-slate-600">—</span>}
                </p>
              </div>
              <div className="sm:col-span-2 border-t border-slate-800 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Paystack customer code</p>
                  {firmRow?.paystack_customer_code ? (
                    <button
                      onClick={() => copy(firmRow.paystack_customer_code, 'Customer code')}
                      className="inline-flex items-center gap-1.5 text-xs font-mono text-slate-300 hover:text-white"
                    >
                      {firmRow.paystack_customer_code}
                      <Copy size={11} className="text-slate-500" />
                    </button>
                  ) : <p className="text-slate-600 text-xs">—</p>}
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Paystack subscription code</p>
                  {firmRow?.paystack_subscription_code ? (
                    <button
                      onClick={() => copy(firmRow.paystack_subscription_code, 'Subscription code')}
                      className="inline-flex items-center gap-1.5 text-xs font-mono text-slate-300 hover:text-white"
                    >
                      {firmRow.paystack_subscription_code}
                      <Copy size={11} className="text-slate-500" />
                    </button>
                  ) : <p className="text-slate-600 text-xs">—</p>}
                </div>
              </div>
            </div>
          </div>

          {/* ── Plan capacity card ───────────────────────────────
              Super-admin-only controls to set the firm's seat cap,
              base storage limit, and storage add-on. These match the
              SQL RPCs from migrations 141 / 143 — every change here
              is gated server-side by the is_super_admin check. */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
              <HardDrive size={14} className="text-indigo-400" />
              Plan capacity
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              Adjust seat and storage caps for this firm. NULL = unlimited (enterprise).
              Storage add-ons stack on top of the base limit.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              {/* Seats */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <UsersIcon size={12} /> Seat limit
                </div>
                <p className="text-lg font-semibold text-white">
                  {s.seat_limit != null
                    ? <>{s.seats_in_use ?? 0} <span className="text-slate-500">/</span> {s.seat_limit}</>
                    : <span className="text-slate-400">Unlimited</span>}
                </p>
                <button
                  onClick={handleSetSeatLimit}
                  disabled={working}
                  className="mt-3 w-full text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white transition disabled:opacity-50"
                >
                  Change seat limit
                </button>
              </div>

              {/* Base storage */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <HardDrive size={12} /> Base storage (plan)
                </div>
                <p className="text-lg font-semibold text-white">
                  {s.storage_limit_mb != null
                    ? <>{(s.storage_limit_mb / 1024).toFixed(s.storage_limit_mb >= 10240 ? 0 : 1)} <span className="text-slate-500 text-sm">GB</span></>
                    : <span className="text-slate-400">Unlimited</span>}
                </p>
                <button
                  onClick={handleSetStorageLimit}
                  disabled={working}
                  className="mt-3 w-full text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white transition disabled:opacity-50"
                >
                  Change base storage
                </button>
              </div>

              {/* Storage add-on */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <Plus size={12} /> Storage add-on
                </div>
                <p className="text-lg font-semibold text-white">
                  {Number(s.storage_addon_mb || 0) > 0
                    ? <>+{(s.storage_addon_mb / 1024).toFixed(1)} <span className="text-slate-500 text-sm">GB</span></>
                    : <span className="text-slate-600">None purchased</span>}
                </p>
                <button
                  onClick={handleAddStorageAddon}
                  disabled={working || s.storage_limit_mb == null}
                  title={s.storage_limit_mb == null ? 'Add-ons only apply to capped plans' : ''}
                  className="mt-3 w-full text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white transition disabled:opacity-50"
                >
                  Add storage
                </button>
              </div>
            </div>
            {s.storage_limit_mb != null && (
              <p className="text-xs text-slate-500 mt-4">
                Effective limit:{' '}
                <span className="text-slate-300 font-medium">
                  {(((s.storage_limit_mb || 0) + (s.storage_addon_mb || 0)) / 1024).toFixed(1)} GB
                </span>{' '}
                (base {((s.storage_limit_mb || 0) / 1024).toFixed(1)} GB
                {Number(s.storage_addon_mb || 0) > 0 ? ` + add-on ${((s.storage_addon_mb || 0) / 1024).toFixed(1)} GB` : ''})
              </p>
            )}
          </div>

          {/* Firm contact details */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-white mb-4">Firm details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                { icon: Mail,     label: 'Email',    value: firmRow?.email },
                { icon: Phone,    label: 'Phone',    value: firmRow?.phone },
                { icon: MapPin,   label: 'Address',  value: [firmRow?.address, firmRow?.city, firmRow?.state, firmRow?.country].filter(Boolean).join(', ') || null },
                { icon: Globe,    label: 'Website',  value: firmRow?.website },
                { icon: Calendar, label: 'Trial ends', value: firmRow?.trial_ends_at ? new Date(firmRow.trial_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null },
                { icon: Calendar, label: 'Plan',     value: firmRow?.subscription_status ? firmRow.subscription_status.charAt(0).toUpperCase() + firmRow.subscription_status.slice(1) : null },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                  <Icon size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-slate-200">{value || <span className="text-slate-600">—</span>}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team members moved to dedicated Users tab — superseded by
              the richer view there (status chips, pending-invite
              flags, super-admin badge, join date). The Overview
              keeps the "Users" stat card at the top so the count
              is still one click away. */}
        </div>
      )}

      {/* Users tab — full picture of who has access to this firm.
          Shows every public.users row (firm staff only — portal
          clients live in public.contacts and surface under the
          Contacts tab). Rows are sorted by last_login desc so
          recently-active accounts surface first. */}
      {tab === 'Users' && (
        <div className="space-y-4">
          {/* Seat summary header. Mirrors the Plan-capacity card
              numbers so super-admins can spot over-cap firms (a
              data point billing ops needs when chasing upsells). */}
          {(s.seat_limit != null || (s.users || []).length > 0) && (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <UsersIcon size={14} className="text-indigo-400" />
                <span className="font-medium">{(s.users || []).length}</span>
                <span className="text-slate-500">user{(s.users || []).length === 1 ? '' : 's'} in firm</span>
              </div>
              {s.seat_limit != null && (
                <div className="text-xs text-slate-400">
                  Seats: <span className="text-white font-medium">{s.seats_in_use ?? 0}</span>
                  <span className="text-slate-500"> / {s.seat_limit}</span>
                  {(s.seats_in_use ?? 0) > s.seat_limit && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 text-[10px] uppercase tracking-wide font-medium">
                      Over cap
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            {!s.users?.length ? (
              <div className="text-center py-16 text-slate-500">
                No users yet. The firm hasn't completed onboarding.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="text-left px-5 py-3 font-medium">Role</th>
                    <th className="text-left px-5 py-3 font-medium">Status</th>
                    <th className="text-left px-5 py-3 font-medium">Joined</th>
                    <th className="text-left px-5 py-3 font-medium">Last login</th>
                    <th className="text-right px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {s.users.map((u) => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || '—';
                    const isPending = u.email_confirmed === false && !u.last_login;
                    // user.status (from public.users.status) is the
                    // explicit staff-level state when present. Falls
                    // back to email_confirmed/last_login heuristic
                    // when the column is missing — see mig 160.
                    const isSuspended = u.status && u.status.toLowerCase() === 'suspended';
                    return (
                      <tr key={u.id} className="hover:bg-slate-800/40 transition">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium">{name}</span>
                            {u.is_super_admin && (
                              <span
                                title="System super-admin — has access across all firms"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-900/40 text-purple-300 text-[10px] uppercase tracking-wide font-medium"
                              >
                                <ShieldCheck size={10} /> Super
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{u.email || <span className="text-slate-600">no email</span>}</p>
                        </td>
                        <td className="px-5 py-3">
                          {u.role
                            ? <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ROLE_COLOR[u.role] || 'bg-slate-700 text-slate-300'}`}>{u.role}</span>
                            : <span className="text-slate-600 text-xs">—</span>}
                        </td>
                        <td className="px-5 py-3">
                          {isSuspended ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-red-900/40 text-red-300">
                              <Ban size={10} /> Suspended
                            </span>
                          ) : isPending ? (
                            <span
                              title="Invited but hasn't clicked the activation link yet"
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-amber-900/40 text-amber-300"
                            >
                              <MailWarning size={10} /> Pending invite
                            </span>
                          ) : userIsActive(u) ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-900/40 text-emerald-300">
                              <CheckCircle size={10} /> Active
                            </span>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-slate-500 text-xs">
                          {u.created_at ? formatDate(u.created_at) : '—'}
                        </td>
                        <td className="px-5 py-3 text-slate-500 text-xs">
                          {u.last_login ? formatDate(u.last_login) : <span className="text-slate-600">Never</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => handleResetPassword(u)}
                            disabled={working || !u.email}
                            title={u.email ? 'Send a password-reset email to this user' : 'No email on file'}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-slate-800 hover:bg-amber-900/40 hover:text-amber-300 text-slate-400 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <KeyRound size={11} /> Reset password
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Portal-client hint — easy to confuse "users" with "clients
              who have a portal login". Spell it out so a super-admin
              doesn't wonder where the portal users are. */}
          <p className="text-xs text-slate-500">
            Portal clients (firm contacts with their own login) live under <span className="text-slate-300">Contacts</span> — look for the green "Active" badge in the Portal column.
          </p>
        </div>
      )}

      {/* Matters */}
      {tab === 'Case Files' && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          {matters.length === 0 ? (
            <div className="text-center py-16 text-slate-500">No case files yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Case File</th>
                  <th className="text-left px-5 py-3 font-medium">Client</th>
                  <th className="text-left px-5 py-3 font-medium">Practice area</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                  <th className="text-left px-5 py-3 font-medium">Opened</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {matters.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-800/40 transition">
                    <td className="px-5 py-3">
                      <p className="font-medium text-white">{m.title}</p>
                      <p className="text-xs text-slate-500">{m.matter_number}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-300">
                      {m.client ? `${m.client.first_name || ''} ${m.client.last_name || ''}`.trim() || m.client.organization || '—' : '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-400">{m.practice_area || '—'}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${MATTER_STATUS_COLOR[m.status] || 'bg-slate-700 text-slate-400'}`}>
                        {m.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(m.open_date || m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Contacts */}
      {tab === 'Contacts' && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          {contacts.length === 0 ? (
            <div className="text-center py-16 text-slate-500">No contacts yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Name</th>
                  <th className="text-left px-5 py-3 font-medium">Type</th>
                  <th className="text-left px-5 py-3 font-medium">Email</th>
                  <th className="text-left px-5 py-3 font-medium">Phone</th>
                  <th className="text-left px-5 py-3 font-medium">Portal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {contacts.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition">
                    <td className="px-5 py-3">
                      <p className="font-medium text-white">
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.organization || '—'}
                      </p>
                      {c.organization && (c.first_name || c.last_name) && (
                        <p className="text-xs text-slate-500">{c.organization}</p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-400 capitalize">{c.contact_type?.replace('_', ' ')}</td>
                    <td className="px-5 py-3 text-slate-400">{c.email || '—'}</td>
                    <td className="px-5 py-3 text-slate-400">{c.phone || '—'}</td>
                    <td className="px-5 py-3">
                      {c.portal_enabled
                        ? <span className="text-xs text-emerald-400">Active</span>
                        : <span className="text-xs text-slate-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invoices */}
      {tab === 'Invoices' && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          {invoices.length === 0 ? (
            <div className="text-center py-16 text-slate-500">No invoices yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Invoice</th>
                  <th className="text-left px-5 py-3 font-medium">Case File</th>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Due</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                  <th className="text-right px-5 py-3 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-800/40 transition">
                    <td className="px-5 py-3 font-medium text-white">#{inv.invoice_number || inv.id.slice(0,8)}</td>
                    <td className="px-5 py-3 text-slate-400">{inv.matter?.title || '—'}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(inv.invoice_date || inv.created_at)}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(inv.due_date)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${INVOICE_STATUS_COLOR[inv.status] || 'bg-slate-700 text-slate-400'}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-white">{formatMoney(inv.total_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Capacity picker modals (seat / base storage / add-on).
          One modal component, three variants — the dropdown
          presets adjust based on the active variant. */}
      <CapacityPickerModal
        open={capacityModal === 'seat'}
        onClose={() => setCapacityModal(null)}
        variant="seat"
        currentValue={firmRow?.seat_limit ?? null}
        onSubmit={handleSubmitSeatLimit}
        working={working}
      />
      <CapacityPickerModal
        open={capacityModal === 'storage-base'}
        onClose={() => setCapacityModal(null)}
        variant="storage-base"
        currentValue={
          stats?.storage_limit_mb != null
            ? Math.round((stats.storage_limit_mb / 1024) * 10) / 10
            : null
        }
        currentAddon={stats?.storage_addon_mb || 0}
        onSubmit={handleSubmitStorageLimit}
        working={working}
      />
      <CapacityPickerModal
        open={capacityModal === 'storage-addon'}
        onClose={() => setCapacityModal(null)}
        variant="storage-addon"
        currentValue={null}  /* always start fresh; add-on stacks */
        currentAddon={stats?.storage_addon_mb || 0}
        onSubmit={handleSubmitStorageAddon}
        working={working}
      />
    </div>
  );
}
